import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image, Gamepad2, Settings, LogOut, Home } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
interface MainMenuPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenControls: () => void;
  onOpenMediaGallery: () => void;
  onOpenSettings: () => void;
}
export function MainMenuPanel({
  isOpen,
  onClose,
  onOpenControls,
  onOpenMediaGallery,
  onOpenSettings
}: MainMenuPanelProps) {
  const {
    isDark
  } = useTheme();
  const handleGoToInterior = () => {
    window.location.href = '/interior/login';
  };
  const menuItems = [{
    id: 'media',
    label: 'Media Gallery',
    icon: Image,
    onClick: () => {
      onClose();
      onOpenMediaGallery();
    }
  }, {
    id: 'controls',
    label: 'Controls',
    icon: Gamepad2,
    onClick: () => {
      onClose();
      onOpenControls();
    }
  }, {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    onClick: () => {
      onClose();
      onOpenSettings();
    }
  }, {
    id: 'exit',
    label: 'Exit Application',
    icon: LogOut,
    onClick: () => console.log('Exit'),
    danger: true
  }];
  return <AnimatePresence data-id="element-920">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" data-id="element-921" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className={`fixed top-0 right-0 bottom-0 w-[400px] backdrop-blur-xl shadow-2xl z-[70] flex flex-col transition-colors duration-300 ${isDark ? 'bg-slate-900/70 border-l border-white/10' : 'bg-white/70 border-l border-white/40'}`} data-id="element-922">
            {/* Header */}
            <div className={`p-6 transition-colors duration-300 ${isDark ? 'border-b border-white/10 bg-slate-900/50' : 'border-b border-white/40 bg-white/50'}`} data-id="element-923">
              <div className="flex items-center justify-between" data-id="element-924">
                <div className="flex items-center gap-4" data-id="element-925">
                  <h2 className={`text-xl font-light ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-926">
                    <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-927">
                      Menu
                    </span>
                  </h2>

                  {/* Interior Button */}
                  <button onClick={handleGoToInterior} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white text-sm font-medium rounded-lg border border-amber-400/30 transition-all shadow-lg hover:shadow-xl" data-id="element-928">
                    <Home className="w-4 h-4" data-id="element-929" />
                    <span data-id="element-930">Interior</span>
                  </button>
                </div>

                <button onClick={onClose} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-white/60 text-gray-500 hover:text-gray-900'}`} data-id="element-931">
                  <X className="w-5 h-5" data-id="element-932" />
                </button>
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex-1 p-6 space-y-3" data-id="element-933">
              {menuItems.map((item, index) => {
            const Icon = item.icon;
            return <motion.button key={item.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} onClick={item.onClick} className={`
                      w-full flex items-center gap-4 p-4 rounded-xl transition-all
                      ${item.danger ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400' : isDark ? 'bg-slate-800/50 border border-white/5 hover:bg-slate-800 hover:border-cyan-500/30 text-white' : 'bg-white/50 border border-white/40 hover:bg-white/80 hover:border-teal-500/30 text-gray-900'}
                    `} data-id="element-934">
                    <div className={`
                      p-3 rounded-lg transition-colors
                      ${item.danger ? 'bg-red-500/20' : isDark ? 'bg-slate-900/50' : 'bg-white/60'}
                    `} data-id="element-935">
                      <Icon className={`w-6 h-6 ${item.danger ? 'text-red-400' : isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-936" />
                    </div>
                    <span className="text-base font-medium" data-id="element-937">{item.label}</span>
                  </motion.button>;
          })}
            </div>

            {/* Footer Info */}
            <div className={`p-6 transition-colors duration-300 ${isDark ? 'border-t border-white/10 bg-slate-900/50' : 'border-t border-white/40 bg-white/50'}`} data-id="element-938">
              <div className={`text-xs space-y-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-939">
                <p data-id="element-940">Magic Patterns Subdivision Explorer</p>
                <p data-id="element-941">Version 1.0.0</p>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}